import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Mail, Phone, Calendar, Users, Sparkles, MapPin, History, Edit, Clock } from "lucide-react";
import PipelineStatut, { PIPELINE, BadgeStatut } from "./PipelineStatut";

const BORDURE = {
  lead: "border-l-purple-500", prospect: "border-l-blue-500",
  negociation: "border-l-amber-500", valide: "border-l-green-500", refuse: "border-l-red-500",
};

function Tuile({ icone: I, fond, teinte, libelle, valeur }) {
  if (!valeur) return null;
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
      <div className={"p-2 rounded-lg " + fond}>
        <I className={"w-4 h-4 " + teinte} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{libelle}</p>
        <p className="text-sm font-medium break-words">{valeur}</p>
      </div>
    </div>
  );
}

function dateFr(v) {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString("fr-FR");
}

const NOMS = "Nom|E-?mail|T[ée]l[ée]phone|Date|Sp[ée]cialit[ée]s?|Nombre de participants|Message";

function parseDemande(txt) {
  const out = {};
  if (!txt) return out;
  const re = new RegExp("(" + NOMS + ")\\s*:\\s*([\\s\\S]*?)(?=\\s*(?:" + NOMS + ")\\s*:|$)", "gi");
  let m;
  while ((m = re.exec(txt)) !== null) {
    const k = m[1].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z]/g, "");
    out[k] = m[2].trim();
  }
  return out;
}

export default function ListeFiches({ liste, vide, onHistorique, onEdit, onStatut }) {
  if (!liste || liste.length === 0) {
    return (
      <Card className="bg-white border-[#E5E0DC]">
        <CardContent className="p-8 text-center text-muted-foreground">{vide}</CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      {liste.map((c) => {
        const st = c.statut || "prospect";
        const nom = c.nom_contact || c.nom || "Sans nom";
        const n = c.historique_contacts?.length || 0;
        const d = parseDemande(c.notes);
        return (
          <Card key={c.id}
            className={"bg-white border-[#E5E0DC] border-l-4 shadow-sm hover:shadow-md transition-shadow " + (BORDURE[st] || "")}>
            <CardContent className="p-4 sm:p-5 space-y-4">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <span className="font-bold text-lg text-secondary">
                      {nom.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-base sm:text-lg font-bold text-secondary break-words">{nom}</h3>
                    {c.nom_entreprise && c.nom_entreprise !== "Non renseigné" && (
                      <p className="text-sm text-muted-foreground break-words">{c.nom_entreprise}</p>
                    )}
                  </div>
                  <BadgeStatut statut={st} />
                  {c.created_by === "email_automatique" && (
                    <Badge variant="outline" className="border-blue-300 text-blue-600 bg-blue-50">
                      <Mail className="w-3 h-3 mr-1" /> Via email
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
                  {c.created_at && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Demande du {dateFr(c.created_at)}
                    </span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                <Tuile icone={Mail} fond="bg-purple-100" teinte="text-purple-600"
                  libelle="Email" valeur={c.email || d.email} />
                <Tuile icone={Phone} fond="bg-green-100" teinte="text-green-600"
                  libelle="Telephone" valeur={c.telephone || d.telephone} />
                <Tuile icone={MapPin} fond="bg-gray-200" teinte="text-gray-600"
                  libelle="Ville" valeur={c.ville} />
                <Tuile icone={Calendar} fond="bg-orange-100" teinte="text-orange-600"
                  libelle="Date evenement" valeur={c.date_evenement || d.date} />
                <Tuile icone={Users} fond="bg-blue-100" teinte="text-blue-600"
                  libelle="Participants" valeur={(c.nombre_participants || d.nombredeparticipants) ? (c.nombre_participants || d.nombredeparticipants) + " personnes" : ""} />
                <Tuile icone={Sparkles} fond="bg-yellow-100" teinte="text-yellow-600"
                  libelle="Specialites souhaitees" valeur={c.specialites || d.specialite || d.specialites} />
              </div>

              {(d.message || c.notes) && (
                <div className="rounded-lg bg-gray-50 border border-[#E5E0DC] p-3">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Message du client</p>
                  <p className="text-sm text-secondary leading-relaxed whitespace-pre-line line-clamp-4">
                    {d.message || c.notes}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-[#E5E0DC]">
                <PipelineStatut statut={st} onChange={(s) => onStatut(c, s)} />
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => onHistorique(c)}
                    className="border-blue-400 text-blue-600 hover:bg-blue-50">
                    <History className="w-4 h-4 mr-1" /> Historique
                    {n > 0 && (
                      <Badge className="ml-1 bg-blue-500 text-white text-xs px-1.5 py-0">{n}</Badge>
                    )}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onEdit(c)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
